import Image from 'next/image';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';

import { CHROME_WEB_STORE_URL } from '@/constants/browserExtension';

/*
  This example requires Tailwind CSS v2.0+ 
  
  This example requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/line-clamp'),
    ],
  }
  ```
*/

interface WhatsNewMessage {
  href: string;
  landingPageHref?: string;
  sender: string;
  time: string;
  datetime: string;
  preview?: string;
  video?: string;
  /** Frame shown before an mp4 `video` starts playing. */
  poster?: string;
  isPortrait?: boolean;
  image?: string;
  sampleImages?: string[];
  openInNewTab?: boolean;
}

interface LazyMediaProps {
  children: React.ReactNode;
  rootMargin?: string;
}

const LazyMedia = ({ children, rootMargin = '200px' }: LazyMediaProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isVisible) return;
    if (typeof window === 'undefined') {
      setIsVisible(true);
      return;
    }
    if (!ref.current) return;
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return <div ref={ref}>{isVisible ? children : null}</div>;
};
export const messages: WhatsNewMessage[] = [
  {
    href: '/tts',
    landingPageHref: '/text-to-speech',
    sender: '🎭 New: AI Voices That Laugh, Whisper and Sigh',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Our text to speech voices now act out your directions instead of reading them aloud.<br/><br/>
    • <b>Audio Tags</b> – Put a direction in square brackets, like [laughs], [whispers] or [sighs], and the voice performs it<br/>
    • <b>Real Emotion</b> – Excited, sad, angry, sarcastic or nervous: the same voice can read one line many ways<br/>
    • <b>Long Scripts</b> – Up to 50,000 characters in one go<br/>
    • <b>1,900+ Voices in 70+ Languages</b> – Or use your own cloned voice<br/><br/>
    Open <b>Text to Speech</b>, pick a voice and add a tag to your script. Watch the video to hear what they can do.<br/><br/>`,
    video: 'QXnphKfFTTo',
  },
  {
    href: '/agents',
    landingPageHref: '/chatbots',
    sender: '🎙️ New: Voice Mode for AI Agents — Your Agent Can Talk Now',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Your AI Agents can now take live voice calls. Visitors tap the call button in your website chat and talk to your agent out loud, in real time.<br/><br/>
    • <b>Answers From Your Knowledge</b> – The same docs, prices and policies your agent already uses in chat<br/>
    • <b>Natural Conversations</b> – Callers can interrupt mid-sentence and the agent keeps up<br/>
    • <b>Almost 2,000 Voices</b> – Pick one from the voice library or use your own cloned voice<br/>
    • <b>Speaks Their Language</b> – Spanish, Hindi, Japanese and many more<br/>
    • <b>Full Transcripts</b> – Every call lands in Conversations with a complete transcript<br/>
    • <b>Make It Yours</b> – Voice model, voice instructions, speaking speed and how long it waits before replying<br/><br/>
    Open your agent, go to <b>Voice</b> and flip the switch. Try it free in the Playground on any plan; voice calls on your website are available on Unlimited 200 and higher plans.<br/><br/>`,
    video: 'https://media.easy-peasy.ai/landing/agent-voice-mode-whats-new.mp4',
    poster:
      'https://media.easy-peasy.ai/landing/agent-voice-mode-whats-new-poster.jpg',
  },
  {
    href: '/browser-extension',
    landingPageHref: '/marky',
    sender: '🌐 New: Marky for Chrome — Marky Works in Your Own Browser',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `The Easy-Peasy.AI Browser Agent extension is live on the Chrome Web Store. Marky can now browse in <b>your own Chrome</b> instead of a cloud browser.<br/><br/>
    • <b>Uses Your Logins</b> – LinkedIn, dashboards, any site you're already signed in to. No passwords shared with Marky<br/>
    • <b>Watch and Take Over</b> – Pages open in a tab you can see and step into at any time<br/>
    • <b>You Approve Every Site</b> – Marky asks before working on a new site: allow once, always, or deny<br/>
    • <b>My Browser Connector</b> – Turn it on or off from Connectors in Marky, with a live Online/Offline status<br/>
    • <b>Any Chromium Browser</b> – Chrome, Edge, Brave and Arc<br/><br/>
    <a href="${CHROME_WEB_STORE_URL}" target="_blank" rel="noopener noreferrer"><u>Add it from the Chrome Web Store</u></a>, connect your browser in one click, then ask Marky something like: "Open my LinkedIn feed and summarize the top posts."<br/><br/>`,
    image:
      'https://media.easy-peasy.ai/landing/whats-new-marky-chrome-extension.png',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: "🌌 New: GPT-6 Astra — OpenAI's Most Capable Model",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `OpenAI's GPT-6 Astra is now available in Marky!<br/><br/>
    Built for the hardest end-to-end work: complex reasoning, coding, computer use, research, and document creation.<br/><br/>
    <b>Highlights:</b><br/>
    • 1.05M-token context window and 128K output<br/>
    • Always-on reasoning with effort levels up to a new <b>Max</b> tier (GPT-6 Astra (Max))<br/>
    • Strong agentic behaviour: infers intent and biases towards action instead of over-asking<br/>
    • Internet access, image generation/editing, Python tools, and vision<br/><br/>
    Try GPT-6 Astra now in Marky chat!<br/><br/>`,
    image:
      'https://images.ctfassets.net/kftzwdyauwt9/6ibCaqsoO7F6XCNCIO8zaZ/9475cdebc1f0af100414f1c85860fc4c/Hero_16x9.png?w=1600&h=900&fit=fill',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: "🚀 New: Claude Opus 5 - Anthropic's Newest Flagship Model",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's newest flagship Opus model Claude Opus 5 is now available (July 2026)!<br/><br/>
    • <b>A Step Change Over Opus 4.8</b> – Stronger deep reasoning, agentic coding, and long-horizon work — at the same price<br/>
    • <b>Built for Complex Coding</b> – Multi-file features, larger refactors, and end-to-end feature work it actually finishes<br/>
    • <b>Sharper Bug Finding</b> – High precision and high recall on code review, even at lower reasoning effort<br/>
    • <b>1M Context Window</b> – Process massive documents, codebases, and datasets in a single prompt (128K output)<br/>
    • <b>Adaptive Thinking</b> – Selectable effort levels (Low → Max); Low and Medium punch well above their weight on this model<br/><br/>
    🧠 1M context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python · 👁️ Vision<br/><br/>
    Available in both standard and reasoning variants!<br/><br/>`,
    image:
      'https://www.anthropic.com/_next/image?url=https%3A%2F%2Fwww-cdn.anthropic.com%2Fimages%2F4zrzovbb%2Fwebsite%2Fa9007019094f217e98cb8261a2765d7646c01708-2600x1392.png&w=3840&q=75',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: "🌙 New: Kimi K3 — Moonshot's 1M-Context Open Flagship",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Moonshot AI's Kimi K3 is now available in Marky!<br/><br/>
    The largest open-weights model ever released — a 2.8-trillion-parameter Mixture-of-Experts model built for long-horizon coding and agentic work.<br/><br/>
    <b>Highlights:</b><br/>
    • <b>1M-token context window</b> for huge codebases and documents<br/>
    • Always-on step-by-step reasoning<br/>
    • #1 on Arena.AI's Frontend Code Arena; top-3 on GDPval-AA v2 real-world work benchmark, ahead of Claude Opus 4.8<br/>
    • Internet access, image generation/editing, Python tools, and vision in Marky<br/><br/>
    Try Kimi K3 now in Marky chat!<br/><br/>`,
    image: 'https://pbs.twimg.com/media/HNYIfg9bgAAruXL?format=jpg&name=large',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '☀️ New: GPT-5.6 — Sol, Terra & Luna',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `OpenAI's GPT-5.6 family is now available in Marky!<br/><br/>
    • <b>GPT-5.6 Sol</b> – Frontier capability for complex professional work, coding, research, and demanding agentic tasks<br/>
    • <b>GPT-5.6 Terra</b> – Strong intelligence with a balanced price for everyday professional workloads<br/>
    • <b>GPT-5.6 Luna</b> – The fastest and most affordable GPT-5.6 model for high-volume work<br/><br/>
    <b>What's new:</b><br/>
    • Up to 1.05M tokens of context and 128K output<br/>
    • New maximum reasoning effort for the hardest tasks<br/>
    • Improved coding, professional work, tool use, and visual design judgment<br/>
    • Internet access, image generation/editing, Python tools, and vision<br/><br/>
    Try GPT-5.6 now in Marky chat!<br/><br/>`,
    image:
      'https://images.ctfassets.net/kftzwdyauwt9/5uOBQ9CN3HA6yzWugjzzYZ/d65727d40c567318f9b366349a1b12a3/OAI_ChatGPTWork_ModelBlog_OpenGraph_16x9_1200x630.png?w=3840&q=90&fm=webp',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: "🚀 New: Claude Sonnet 5 — Anthropic's Most Agentic Sonnet Yet",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's most agentic Sonnet model is now available (June 2026)!<br/><br/>
    • <b>A Drop-In Upgrade to Sonnet 4.6</b> – The best combination of speed and intelligence, at the same price<br/>
    • <b>Approaches Opus 4.8 Quality</b> – Closes the gap with Opus-class models on coding, reasoning, and agentic tasks — at a fraction of the cost<br/>
    • <b>Most Agentic Sonnet Yet</b> – Stronger planning, tool use, and long-horizon autonomous work<br/>
    • <b>Adaptive Thinking</b> – Thinks by default with selectable effort, or pick the Reasoning variant for the deepest chains of thought<br/>
    • <b>1M Context Window</b> – Process massive documents, codebases, and datasets in a single prompt (128K output)<br/><br/>
    🧠 1M context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python · 👁️ Vision<br/><br/>
    Available in both standard and reasoning variants!<br/><br/>`,
    image:
      'https://www.anthropic.com/_next/image?url=https%3A%2F%2Fwww-cdn.anthropic.com%2Fimages%2F4zrzovbb%2Fwebsite%2F9941d610909f28a504e16dd5af823df172ec6035-2600x1234.png&w=3840&q=75',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '⏰ New: Schedule Tasks in Marky Agent',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Marky can now run tasks automatically on your schedule!<br/><br/>
    • <b>One-Time or Recurring Runs</b> – Schedule tasks once, daily, weekly, or monthly<br/>
    • <b>Autonomous Agent Execution</b> – Marky runs the prompt in the background without needing you online<br/>
    • <b>Results in Chat</b> – Each run is saved back to a Marky conversation so you can review the full output<br/>
    • <b>Email Notifications</b> – Get a notification with a direct link when a scheduled task finishes<br/>
    • <b>Easy Management</b> – View, pause, edit, resume, or delete scheduled tasks from the Scheduled Tasks panel<br/><br/>
    Try prompts like: "Every morning, research AI news and summarize the top stories."<br/><br/>`,
    image: '/images/whats-new-marky-scheduled-tasks.png',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender:
      '🚀 New AI Models: GLM 5.2, Kimi K2.7 Code, MiniMax M3 & Qwen 3.7 Plus',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Four new frontier models are now available in Marky chat!<br/><br/>
    • <b>GLM 5.2</b> – Z.ai's latest flagship with a 1M-token context window and advanced multi-effort coding for long-horizon tasks, powered by the new IndexShare architecture<br/>
    • <b>Kimi K2.7 Code</b> – Coding-focused agentic model built on Kimi K2.6 for long-horizon software engineering workflows<br/>
    • <b>MiniMax M3</b> – Native multimodal MoE model with a 1M context window for text, image, video, and agentic coding tasks<br/>
    • <b>Qwen 3.7 Plus</b> – Alibaba's latest flagship model with strong reasoning and coding capabilities<br/><br/>
    🧠 Long context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python · 👁️ Vision<br/><br/>
    Try the new models now in Marky chat!<br/><br/>`,
    image: '/images/whats-new-ai-models-kimi-minimax-qwen.png',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: "🚀 New: Claude Fable 5 - Anthropic's Most Capable Model",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's most capable widely released model Claude Fable 5 is now available (June 2026)!<br/><br/>
    • <b>Anthropic's Most Capable Model</b> – Built for the most demanding reasoning and long-horizon agentic work<br/>
    • <b>Always-On Adaptive Thinking</b> – Thinks by default with selectable effort levels (Low → Max) to balance reasoning depth against latency<br/>
    • <b>1M Context Window</b> – Process massive documents, codebases, and datasets in a single prompt (128K output)<br/>
    • <b>Built for Agents</b> – Stronger long-horizon agentic coding, sharper judgment, and reliable tool use<br/><br/>
    🧠 1M context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python · 👁️ Vision<br/><br/>
    Available in both standard and reasoning variants!<br/><br/>`,
    image:
      'https://www.anthropic.com/_next/image?url=https%3A%2F%2Fwww-cdn.anthropic.com%2Fimages%2F4zrzovbb%2Fwebsite%2F1e65982497d7d4891219ed0e83141625a291b860-2600x2870.png&w=3840&q=75',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: "🚀 New: Claude Opus 4.8 - Anthropic's Newest Flagship Model",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's newest flagship model Claude Opus 4.8 is now available (May 2026)!<br/><br/>
    • <b>Anthropic's Most Capable Model</b> – Reported to surpass GPT-5.5 and Gemini 3.1 Pro across coding, agentic, and reasoning tasks<br/>
    • <b>Long-Horizon Agentic Coding</b> – Better long-context handling, fewer compactions, and fewer skipped tool calls than Opus 4.7<br/>
    • <b>More Honest</b> – Clearer, more reliable reporting of its own progress and limitations<br/>
    • <b>1M Context Window</b> – Process massive documents, codebases, and datasets in a single prompt (128K output)<br/>
    • <b>Adaptive Thinking</b> – Selectable effort levels (Low → Max), defaulting to High, to balance reasoning depth against latency<br/><br/>
    🧠 1M context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python · 👁️ Vision<br/><br/>
    Available in both standard and reasoning variants!<br/><br/>`,
    image:
      'https://www.anthropic.com/_next/image?url=https%3A%2F%2Fwww-cdn.anthropic.com%2Fimages%2F4zrzovbb%2Fwebsite%2Fa9007019094f217e98cb8261a2765d7646c01708-2600x1392.png&w=3840&q=75',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🤖 New: GPT-5.5 Suite — Instant, Thinking & Pro Models!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `OpenAI's newest frontier model GPT-5.5 is now available!<br/><br/>
    • <b>GPT-5.5 Instant</b> – Faster, smarter everyday model with native computer use<br/>
    • <b>GPT-5.5 Thinking</b> – Medium reasoning effort for stronger multi-step problem solving<br/>
    • <b>GPT-5.5 Pro</b> – Extra-high reasoning effort for maximum performance on complex professional tasks<br/><br/>
    <b>Key Improvements over GPT-5.4:</b><br/>
    • Higher intelligence across reasoning, coding, and agentic benchmarks<br/>
    • New extra-high (xhigh) reasoning effort for the Pro variant<br/>
    • 1M token context window with internet, image generation/editing, and Python tools<br/><br/>
    Try GPT-5.5 now in Marky chat!<br/><br/>`,
    image:
      'https://images.ctfassets.net/kftzwdyauwt9/2Bh47W4cA48dvG5FbDtNQH/5897109fb0de6fc3125c0c35c4f60001/Hero_Art_Card_16x9.jpg?w=1600&h=900&fit=fill',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🐋 New: DeepSeek V4 Pro - Unlimited for 1 Month!',
    time: 'now',
    datetime: new Date().toISOString(),
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/be5f0f04-2ee1-4e41-b860-ebe451a0b065.png',
    preview: `DeepSeek V4 Pro is now available — flagship open-source Mixture-of-Experts model for frontier reasoning, advanced coding, and long-context intelligence!<br/><br/>
    • <b>Unlimited for 1 Month</b> – Use DeepSeek V4 Pro at zero credit cost for the next month<br/>
    • <b>1M Context Window</b> – Handle massive documents, codebases, and datasets in a single prompt<br/>
    • <b>Frontier Reasoning</b> – Intelligence Index of 52 for deep multi-step reasoning and complex agentic workflows<br/>
    • <b>Advanced Coding</b> – Top-tier open-source performance on demanding production workloads<br/>
    • <b>Hybrid Attention Architecture</b> – Dramatically improved long-context efficiency with reduced KV and compute overhead<br/><br/>
    🧠 1M context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python · 💭 Reasoning<br/><br/>
    Try DeepSeek V4 Pro now in Marky chat!<br/><br/>`,
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender:
      "🎨 New: GPT Image 2.5 Flare & Sunburst - OpenAI's Newest Image Models",
    time: 'now',
    datetime: new Date().toISOString(),
    image:
      'https://ir7z8qsacw4tk54b.public.blob.vercel-storage.com/gpt-image-2/hero.png?v=3',
    preview: `OpenAI's new <b>GPT Image 2.5</b> models (September 2026) are live in the AI Image Generator and the image editor!<br/><br/>
    • <b>GPT Image 2.5 Flare</b> – OpenAI's recommended default: higher quality than GPT Image 2 at up to 50% lower latency<br/>
    • <b>GPT Image 2.5 Sunburst</b> – Premium tier for tighter control across multi-step edits, campaign creative, and polished product imagery<br/>
    • <b>More Natural Lighting & Richer Textures</b> – Noticeably more lifelike materials, skin, and fabric<br/>
    • <b>Better Reference Preservation</b> – Keeps the subject from your reference photos intact across edits<br/>
    • <b>Same Credits as GPT Image 2</b> – Low 0.5, Medium 3, High 11 credits per image<br/><br/>
    Try GPT Image 2.5 now in the AI Image Generator!<br><br>`,
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender: "🎨 New: GPT Image 2 - OpenAI's Next-Gen Image Model",
    time: 'now',
    datetime: new Date().toISOString(),
    image:
      'https://ir7z8qsacw4tk54b.public.blob.vercel-storage.com/gpt-image-2/hero.png?v=3',
    preview: `OpenAI's latest image model <b>GPT Image 2</b> is now available in three quality tiers!<br/><br/>
    • <b>Sharper Detail & Realism</b> – Photorealistic output with crisp fabric, hair, and skin textures<br/>
    • <b>Best-in-Class Text Rendering</b> – Accurate typography, logos, and in-image text<br/>
    • <b>Strong Prompt Adherence</b> – Follows complex multi-object prompts with precise composition<br/>
    • <b>Three Quality Tiers</b> – Low (0.5 credits) for drafts, Medium (3 credits) for production, High (11 credits) for maximum fidelity<br/>
    • <b>Native Image Editing</b> – Edit with reference images; preserves subject identity at high fidelity<br/>
    Try it now in the AI Image Generator!<br><br>`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: "🚀 New: Claude Opus 4.7 - Anthropic's Newest Flagship Model",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's newest flagship model Claude Opus 4.7 is now available (April 2026)!<br/><br/>
    • <b>Best-in-Class Coding</b> – 87.6% on SWE-bench Verified (up from 80.8%) and 64.3% on SWE-bench Pro<br/>
    • <b>Agentic Leadership</b> – 69.4% on Terminal-Bench 2.0 and 64.4% on Finance Agent, leading GPT-5.4 and Gemini 3.1 Pro<br/>
    • <b>1M Context Window</b> – Process massive documents, codebases, and datasets in a single prompt (128K output)<br/>
    • <b>Sharper Vision</b> – 3.75MP input resolution, over 3× prior Claude models, for detailed screenshots and diagrams<br/>
    • <b>New xhigh Effort Level</b> – Finer control between high and max for tuning reasoning vs. latency<br/><br/>
    🧠 1M context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python · 👁️ Vision<br/><br/>
    Available in both standard and reasoning variants!<br/><br/>`,
    image:
      'https://www.anthropic.com/_next/image?url=https%3A%2F%2Fwww-cdn.anthropic.com%2Fimages%2F4zrzovbb%2Fwebsite%2F9299f8b86c69359c31d15dbece4545e628bddc34-1920x1080.png&w=3840&q=75',
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator/seedance-2',
    sender: '🎬 New: Seedance 2.0 by ByteDance - Enterprise-Grade AI Video',
    time: 'now',
    datetime: new Date().toISOString(),
    video: 'https://media.easy-peasy.ai/assets/seedance2_hero.mp4',
    preview: `Seedance 2.0 is now available — ByteDance's most powerful video model yet!<br/><br/>
    • <b>Native Audio</b> – Dialogue, foley, and ambient audio generated with the video<br/>
    • <b>Cinematic Camera Control</b> – Hitchcock zoom, tracking shots, orbits, and more<br/>
    • <b>Reference Images</b> – Up to 9 reference images for character consistency<br/>
    • <b>4 Input Modes</b> – Text, image, video reference, and audio-driven generation<br/>
    • <b>Fast Variant</b> – Get results quicker with Seedance 2.0 Fast<br/><br/>
    Available now in the AI Video Generator!`,
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator',
    sender: '🎬 New: Video Keyframes & Reference Images - Control Every Frame!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Take full control of your AI-generated videos with keyframe and reference image support!<br/><br/>
    • <b>First & Last Frame Control</b> – Set both the starting and ending frames for smooth, predictable transitions<br/>
    • <b>Reference Images</b> – Provide up to 3 reference images to guide visual consistency across your video<br/>
    • <b>Better Quality</b> – Maintain character and style consistency throughout the entire clip<br/><br/>
    Available now in the AI Video Generator and through Marky chat!`,
  },
  {
    href: '/creators',
    sender: '🌟 New: Creator Program - Join Easy-Peasy.AI Creators!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Apply to become an Easy-Peasy.AI Creator and grow your audience!<br/><br/>
    • <b>Open to All Creators</b> – Content creators, influencers, and educators are welcome to apply<br/>
    • <b>AI-Powered Tools</b> – Access cutting-edge AI from OpenAI, Anthropic, Google, and more<br/>
    • <b>Simple Application</b> – Just share your email and social links to get started<br/><br/>
    Visit the Creator Program page to apply today!`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '📊 New: Stock Analysis & Financial Data in Marky!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Marky can now analyze stocks and retrieve real-time financial data!<br/><br/>
    • <b>Stock Quotes</b> – Get real-time stock prices, market cap, and key metrics<br/>
    • <b>Financial Statements</b> – Access income statements, balance sheets, and cash flow data<br/>
    • <b>Market Movers</b> – Track top gainers, losers, and trending stocks<br/>
    • <b>Comprehensive Analysis</b> – Get AI-powered insights on any stock or company<br/><br/>
    Just ask Marky about any stock or company to get started!`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🎵 New: Generate Music, Speech & Videos Directly in Chat!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Marky can now create media right inside your conversation!<br/><br/>
    • <b>Music Generation</b> – Describe the mood, genre, and style to create original music tracks<br/>
    • <b>Text-to-Speech</b> – Generate natural-sounding speech with multiple voice options<br/>
    • <b>Video Creation</b> – Generate AI videos without leaving the chat<br/>
    • <b>Inline Playback</b> – Listen and watch generated media directly in the chat<br/><br/>
    Ask Marky to create a song, generate speech, or make a video — all in one conversation!`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🗺️ New: Google Maps Search in Marky!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Marky can now search for places and local businesses using Google Maps!<br/><br/>
    • <b>Find Places</b> – Search for restaurants, shops, attractions, and more<br/>
    • <b>Location-Based</b> – Get results near any location with latitude/longitude support<br/>
    • <b>Business Details</b> – View ratings, addresses, and other place information<br/><br/>
    Ask Marky to find places near you or any location worldwide!`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🧠 New: Memory Import - Bring Your Memories to Marky!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Easily import your memories from other AI providers into Marky!<br/><br/>
    • <b>Copy & Paste Import</b> – Simply paste your exported memories from ChatGPT or other AI assistants<br/>
    • <b>Smart Parsing</b> – Automatically detects and formats imported memories for Marky<br/>
    • <b>Seamless Transition</b> – Keep your personalization when switching to Easy-Peasy.AI<br/><br/>
    Open Marky's memory settings and click Import to get started!`,
  },
  {
    href: '/presets/social-stories-generator',
    sender: '📖 New: Social Stories Generator - AI-Illustrated Stories!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Create personalized illustrated social stories with AI!<br/><br/>
    • <b>Custom Characters</b> – Design characters that match your needs<br/>
    • <b>AI Illustrations</b> – Every story comes with beautiful AI-generated illustrations<br/>
    • <b>Personalized Narratives</b> – Stories tailored to specific situations and learning goals<br/>
    • <b>Easy to Share</b> – Download and share stories with children, students, or clients<br/><br/>
    Perfect for educators, therapists, and parents!`,
  },
  {
    href: '/blog/nano-banana-2-google-ai-image-model',
    openInNewTab: true,
    sender: "🍌 New: Nano Banana 2 - Google's Fastest AI Image Model!",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Nano Banana 2 combines the advanced intelligence of Nano Banana Pro with the lightning-fast speed of Gemini Flash — no more choosing between speed or quality!<br/><br/>
    • <b>Real-Time Knowledge</b> – Powered by live web search for accurate infographics and current data visualizations<br/>
    • <b>Precision Text Rendering</b> – Legible, accurate text in images for marketing materials, banners, and multilingual content<br/>
    • <b>Subject Consistency</b> – Maintain consistent appearance of up to 5 characters and 14 objects across multiple images<br/>
    • <b>4K Quality Output</b> – Vibrant lighting, rich textures, and photorealistic results across multiple aspect ratios<br/>
    • <b>Enhanced Instruction Following</b> – Captures exact mood, style, and composition from detailed prompts<br/><br/>
    Available free on Easy-Peasy.AI in the AI Image Generator, AI Image Editor, and Marky Agent!<br/><br/>`,
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/e898afa5-f739-4db7-813a-10a473b3fe01.png',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: "🚀 New: Claude Opus 4.6 - Anthropic's Most Powerful Model Yet",
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's latest flagship model Claude Opus 4.6 is now available!<br/><br/>
    • <b>1M Context Window</b> – Process massive documents, codebases, and datasets in a single prompt<br/>
    • <b>128K Output Tokens</b> – Generate longer, more detailed responses than ever before<br/>
    • <b>Adaptive Thinking</b> – Dynamically decides when deeper reasoning is needed for optimal cost-quality tradeoffs<br/>
    • <b>State-of-the-Art Benchmarks</b> – 65.4% on TerminalBench, 72.7% on OSWorld, 68.8% on ARC AGI 2<br/>
    • <b>Reasoning Mode</b> – Enable extended thinking for complex multi-step problems<br/><br/>
    🧠 200k context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python<br/><br/>
    Available in both standard and reasoning variants!<br/><br/>`,
    image:
      'https://www.anthropic.com/_next/image?url=https%3A%2F%2Fwww-cdn.anthropic.com%2Fimages%2F4zrzovbb%2Fwebsite%2F0e5c55fa8fd05a893d11168654dc36999e90908b-2600x2968.png&w=3840&q=75',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🚀 New: Kimi K2.5 - Flagship Agentic Model with Vision',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Moonshot AI's flagship agentic model and a new SOTA open model is now available!<br/><br/>
    • <b>Unified Vision & Text</b> – Seamlessly process both images and text in a single model<br/>
    • <b>Thinking & Non-Thinking Modes</b> – Advanced reasoning when you need it, fast responses when you don't<br/>
    • <b>Single & Multi-Agent Execution</b> – Powerful agentic capabilities for complex workflows<br/>
    • <b>256k Context Window</b> – Handle extensive conversations and large documents<br/>
    • <b>State-of-the-Art Performance</b> – New benchmarks in agentic tasks and reasoning<br/><br/>
    🧠 256k context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python · 👁️ Vision<br/><br/>`,
    image:
      'https://cdn.sanity.io/images/pv37i0yn/production/a274895990614c977f3ee80714bfdd2935fc9095-1920x1080.jpg?auto=format',
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator',
    sender:
      '🎬 New: Seedance 1.5 Pro - Direct Every Frame, Sync Every Sound! (7-Day Promo)',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Introducing ByteDance's most advanced video generation model with native audio-visual generation!<br/><br/>
    <b>🎉 PROMOTIONAL PRICING: Just 1 credit per second for the next 7 days!</b><br/><br/>
    • <b>Millisecond Audio-Visual Sync</b> – Native V2A generation with perfectly synchronized ambient sounds, action sounds, music, and human voices<br/>
    • <b>Multi-Person Multilingual Dialogue</b> – Support for English, Mandarin, Japanese, Korean, Spanish, Indonesian with accurate lip sync<br/>
    • <b>Cinematic Narrative Quality</b> – Natural movement, strong visual perception, and subtle character emotions<br/>
    • <b>Flexible Duration</b> – Create 4-12 second videos with synchronized audio<br/>
    • <b>Text & Image to Video</b> – Generate from prompts or transform your images<br/><br/>
    Experience cinematic-grade creative quality at an unbeatable promotional price!`,
    video:
      'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/52804b98-aaf4-4cd4-8ca8-37a7855a025d.mp4',
  },
  {
    href: '/ai-talking-videos',
    landingPageHref: '/ai-videos/talking',
    sender: '🎅 New: Santa Claus AI Actors - Create Holiday Talking Videos!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Spread holiday cheer with our new Santa Claus AI Actors!<br/><br/>
    • <b>4 Unique Santa Voices</b> – Choose from Father Christmas (British storyteller), Mr. Claus (Classic jolly), Nicholas (German), and Santa (French Quebec)<br/>
    • <b>2 Photorealistic Santa Avatars</b> – Classic vintage Coca-Cola style Santa and cozy cabin storyteller Santa<br/>
    • <b>Perfect for Holiday Content</b> – Create personalized holiday greetings, Christmas stories, and festive messages<br/>
    • <b>Multiple Languages</b> – Santa speaks English, German, and French!<br/><br/>
    Try it now: "Ho ho ho! Merry Christmas, dear friend! This is Santa Claus, coming to you from the North Pole through the magic of Easy-Peasy.AI!"`,
    video: 'https://media.easy-peasy.ai/assets/santa.mp4',
  },
  {
    href: '/ai-videos?tab=motion-control',
    landingPageHref: '/ai-video-generator',
    sender: '🎬 New: Motion Control - Transfer Movements from Any Video!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Transform any character image using motion from a reference video!<br/><br/>
    • <b>Motion Transfer</b> – Copy movements, expressions, and actions from any video to your image<br/>
    • <b>Character Animation</b> – Animate portraits, characters, and subjects with realistic movement<br/>
    • <b>Flexible Orientation</b> – Choose video or image orientation for perfect results<br/>
    • <b>720p & 1080p Quality</b> – Standard for speed or Pro for maximum quality<br/>
    • <b>3-30 Second Videos</b> – Create clips from short to medium length<br/><br/>
    Upload a reference video with the motion you want, add your character image, and watch AI bring it to life!`,
    video: 'https://media.easy-peasy.ai/assets/motion.mp4',
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender: '🎬 New: Visualize What Happens Next - AI Story Grid Generator!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Transform a single photo into a cinematic 3x3 story grid showing what happens next!<br/><br/>
    • <b>Story-Driven Sequences</b> – AI analyzes your image and creates 9 narrative scenes flowing like a movie storyboard<br/>
    • <b>Consistent Character & Environment</b> – Maintains the same character, location, and visual style across all 9 frames<br/>
    • <b>Cinematic Quality</b> – Professional camera angles, lighting specs, and technical details for each shot<br/>
    • <b>Powered by Gemini 3.0 + Nano Banana Pro</b> – Uses Google's advanced AI for prompt generation and 4K image creation<br/>
    • <b>Optional Context</b> – Add story mood hints like "mysterious adventure" or "dramatic escape"<br/><br/>
    Upload any photo and watch AI create a visual narrative of what happens next - perfect for storytelling, storyboarding, and creative inspiration!`,
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c48e5913-bfc0-4b10-9888-6ec745424aaf.png',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🤖 New: GPT-5.4 Suite — Instant, Thinking & Pro Models!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `OpenAI's most capable and efficient frontier model is here — GPT-5.4!<br/><br/>
    • <b>GPT-5.4 Instant</b> – Fastest GPT-5.4 for everyday work with native computer use<br/>
    • <b>GPT-5.4 Thinking</b> – Deep reasoning with improved web research and extended context<br/>
    • <b>GPT-5.4 Pro</b> – Maximum performance with record-breaking accuracy<br/><br/>
    <b>Key Improvements over GPT-5.2:</b><br/>
    • 33% fewer errors in individual claims<br/>
    • Native computer use capabilities<br/>
    • 1M token context window (up from 400k)<br/>
    • Significantly improved token efficiency<br/><br/>
    All variants include internet search, image generation/editing, Python tools, and 1M context. Choose the model that fits your needs!<br/><br/>`,
    image:
      'https://images.ctfassets.net/kftzwdyauwt9/rp3qd1SEWW0Slo5V4NiJz/1e6e391508cc0d85fa8458fd8c95608d/5.2-Blog-16x9.png?w=1600&h=900&fit=fill',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🚀 New: Claude Opus 4.5 - Premium Model with Maximum Intelligence',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's latest breakthrough - Claude Opus 4.5 sets new standards across all domains!<br/><br/>
    • <b>Outperforms Human Experts</b> – Scored higher than any human candidate on Anthropic's challenging engineering exam<br/>
    • <b>State-of-the-Art Performance</b> – Industry-leading capabilities across popular benchmarks<br/>
    • <b>Superior Vision & Reasoning</b> – Enhanced visual understanding and advanced multi-step reasoning<br/>
    • <b>Advanced Mathematics</b> – Exceptional performance on complex mathematical tasks<br/>
    • <b>200k Context Window</b> – Handle large documents and extensive conversations<br/><br/>
    🧠 200k context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python<br/><br/>
    Experience the pinnacle of AI intelligence with Claude Opus 4.5!<br/><br/>`,
    image:
      'https://www.anthropic.com/_next/image?url=https%3A%2F%2Fwww-cdn.anthropic.com%2Fimages%2F4zrzovbb%2Fwebsite%2F52303b11db76017fd0c2f73c7fafa5c752515979-2600x2236.png&w=3840&q=75',
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender: '🍌 New: Nano Banana Pro - Advanced AI Image Generation & Editing!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Introducing Nano Banana Pro, powered by Google's Gemini 3 Pro Image - the ultimate solution for complex image generation and editing!<br/><br/>
    • <b>State-of-the-Art Reasoning</b> – Incorporates advanced reasoning capabilities for the most challenging image tasks<br/>
    • <b>Complex Multi-Turn Editing</b> – Best model for complex and multi-turn image generation and editing<br/>
    • <b>Superior Image Quality</b> – Gemini 3 reasoning models think through their process before responding, resulting in improved accuracy and enhanced image quality<br/>
    • <b>Multiple Reference Images</b> – Support for multiple images for mixing, reference, and precise control<br/>
    • <b>10 Aspect Ratios</b> – Wide range of dimensions from square to ultrawide (21:9, 16:9, 3:2, 4:3, 5:4, 1:1, 4:5, 3:4, 2:3, 9:16)<br/>
    • <b>Just 2 Credits</b> – Premium quality at an affordable price<br/><br/>
    Experience the next generation of AI-powered image creation with Nano Banana Pro!<br/><br/>`,
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/2c378f36-be08-4c09-a1e0-7644a6db226f.png',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🤖 New: GPT-5.1 Suite — Instant, Thinking, and Pro!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `OpenAI's smartest chat models just landed on Easy-Peasy.AI! Choose the level of depth you need:<br/><br/>
    • <b>GPT-5.1 Instant</b> – Snappy replies with GPT-5 comprehension<br/>
    • <b>GPT-5.1 Thinking</b> – Adds structured reasoning for research & planning<br/>
    • <b>GPT-5.1 Pro</b> – Deepest multi-step thinking for complex agentic tasks<br/><br/>
    All variants include internet search, image generation/editing, Python tools, and 400k context. Pick the speed-to-cost balance that fits every conversation.<br/><br/>`,
    image:
      'https://images.ctfassets.net/kftzwdyauwt9/2Ek1rUZQWAa5LVpHgeOgg6/3f950eae5ac318c97a8e467a7611c0d3/5-1_Blog_OpenGraph.png',
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator',
    sender: '🎬 New: Hailuo 2.3 - Breakthrough in Body Movement & Realism!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Experience new breakthroughs in body movement, facial expressions, physical realism, and prompt adherence!<br/><br/>
    • <b>Revolutionary Motion Quality</b> – Unprecedented realism in body movements and facial expressions<br/>
    • <b>Hailuo 2.3 Standard</b> – Advanced quality at 6-10 seconds (14-28 credits)<br/>
    • <b>Hailuo 2.3 Pro</b> – Premium quality at 5 seconds (24 credits)<br/>
    • <b>Hailuo 2.3-Fast Pro</b> – Fast and efficient at 6 seconds (16 credits)<br/>
    • <b>Text & Image Support</b> – Create from text prompts or transform your images<br/><br/>
    Two options — Hailuo 2.3 for advanced quality, or Hailuo 2.3-Fast for value and efficiency!`,
    video:
      'https://cdn.hailuoai.video/open-hailuo-video-web/public_assets/compressed_vm23-anno-en.mp4',
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator',
    sender: '🎬 New: LTX-2 - Complete AI Creative Engine with 4K Video!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `LTX-2 is a complete AI creative engine built for real production workflows!<br/><br/>
    • <b>4K Video at 48 FPS</b> – Professional-grade output with stunning clarity<br/>
    • <b>Synchronized Audio & Video</b> – Perfectly matched sound and visuals<br/>
    • <b>LTX-2 Pro</b> – Premium quality at 6-10 seconds (3 credits/sec)<br/>
    • <b>LTX-2 Fast</b> – Rapid generation up to 20 seconds (2 credits/sec)<br/>
    • <b>Multi-Keyframe Conditioning</b> – Frame-coherent storytelling with stylistic consistency<br/>
    • <b>3D Camera Logic</b> – Professional camera movements and depth control<br/>
    • <b>Text & Image Inputs</b> – Shape pacing, motion, and tone with multiple input types<br/><br/>
    Radical efficiency with consumer-grade GPU support — professional results, accessible technology!`,
    video:
      'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/original_cfbd207f-de84-4eac-b2e5-74e56f97d267.mp4',
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator',
    sender: '🎬 New: Google Veo 3.1 - Revolutionary 1080p AI Video Generation!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Experience the latest breakthrough in AI video generation with Google Veo 3.1!<br/><br/>
    • <b>1080p Resolution</b> – Stunning high-definition video output<br/>
    • <b>Text-to-Video & Image-to-Video</b> – Create videos from text or transform your images<br/>
    • <b>First-Last Frame Support</b> – Generate videos between two keyframes for precise control<br/>
    • <b>Flexible Durations</b> – Choose from 4, 6, or 8-second videos<br/>
    • <b>Multiple Aspect Ratios</b> – Support for 16:9, 9:16, and 1:1 formats<br/>
    • <b>Fast & Standard Models</b> – Balance between speed and quality based on your needs<br/><br/>
    Create professional-quality videos with unprecedented control and clarity!`,
    video:
      'https://storage.googleapis.com/falserverless/model_tests/gallery/veo3-1-i2v.mp4',
  },
  {
    href: '/audios',
    landingPageHref: '/ai-transcription',
    sender:
      '🎙️ New: AI Transcription Expanded - TikTok, Instagram, Google Drive & More!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Our AI Transcription just got a massive upgrade with support for multiple new sources!<br/><br/>
    • <b>TikTok Videos</b> – Transcribe any public TikTok video instantly<br/>
    • <b>Instagram Videos & Reels</b> – Convert Instagram content to text effortlessly<br/>
    • <b>Google Drive Files</b> – Import and transcribe files directly from Google Drive<br/>
    • <b>Import Any URL</b> – Paste any publicly accessible audio/video URL to transcribe<br/>
    • <b>YouTube (Already Supported)</b> – Continue transcribing YouTube videos as before<br/><br/>
    From social media to cloud storage, transcribe content from anywhere with AI-powered accuracy!`,
  },
  {
    href: '/ai-images/models/train',
    landingPageHref: '/ai-photo-generator',
    sender:
      '🎨 New: Train AI Models on Your Photos - Create Hyperrealistic Images & Videos!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Upload selfies and create your personal AI model to generate stunning, hyperrealistic content!<br/><br/>
    • <b>Professional Headshots & Instagram Photos</b> – Generate 100s of photos from curated photo packs<br/>
    • <b>AI Photos in Any Pose, Place or Action</b> – Create images of yourself in any scenario imaginable<br/>
    • <b>AI Videos Starring You</b> – Generate videos with yourself as the main character<br/>
    • <b>Talking Videos</b> – Create realistic talking videos using your AI model<br/>
    • <b>One-Time Training</b> – Upload 3-10 selfies once, use your model unlimited times<br/><br/>
    Transform your photos into endless creative possibilities with AI!`,
    sampleImages: [
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/cc925719-29cb-44f1-937e-c872d17718c8.png',
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/809d702b-5261-40af-ba52-b7a86785dd93.png',
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/cabdbd26-cabe-4abc-8c06-e2742bfc1e53.png',
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/95a3db9e-3fcd-42df-a606-cb3740937777.png',
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/0bfe2fc8-c53e-486e-aff1-21ad7e2e56b4.png',
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/9a07dda6-cff9-4688-8f8f-3c5cd22ab664.png',
    ],
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator',
    sender:
      '🎬 New: OpenAI Sora 2 & Sora 2 Pro - Cinematic AI Video Generation',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `OpenAI's groundbreaking Sora 2 video generation models are now available!<br/><br/>
    • <b>Sora 2 & Sora 2 Pro</b> – Revolutionary text-to-video and image-to-video generation<br/>
    • <b>Native Audio Generation</b> – Videos include realistic sound effects and ambient audio<br/>
    • <b>Cinematic Quality</b> – Professional-grade output with stunning visual fidelity<br/>
    • <b>Flexible Durations</b> – Generate 4, 8, or 12-second videos<br/>
    • <b>Multiple Aspect Ratios</b> – Support for 16:9 and 9:16 formats<br/><br/>
    Experience the future of AI-powered video creation with OpenAI Sora 2!`,
    video:
      'https://storage.googleapis.com/falserverless/example_outputs/sora-2-pro-t2v-output.mp4',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender:
      '🚀 New: Claude Sonnet 4.6 - Improved Coding & Long-Context Reasoning',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's latest Claude Sonnet 4.6 is now available!<br/><br/>
    • <b>Claude Sonnet 4.6</b> – Improved coding, computer use, and long-context reasoning<br/>
    • <b>Better Agent Planning</b> – Enhanced abilities for complex agents and knowledge work<br/>
    • <b>Extended Thinking</b> – Adaptive thinking for advanced multi-step reasoning<br/>
    • <b>200k Context Window</b> – Handle large documents and extensive conversations<br/><br/>
    🧠 200k context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🚀 New: Claude Sonnet 4.5 - Best for Complex Agents & Coding',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's latest Claude Sonnet 4.5 is now available!<br/><br/>
    • <b>Claude Sonnet 4.5</b> – Best model for complex agents and coding, with the highest intelligence across most tasks<br/>
    • <b>Superior Coding Performance</b> – Exceptional at coding tasks and technical problem-solving<br/>
    • <b>Advanced Reasoning</b> – Enhanced multi-step reasoning for complex challenges<br/>
    • <b>200k Context Window</b> – Handle large documents and extensive conversations<br/><br/>
    🧠 200k context · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python`,
  },
  {
    href: '/music-generator',
    landingPageHref: '/ai-song-generator',
    sender: '🎵 New: AI Song Generator Version 2.0 - Create Songs with Videos!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Introducing AI Song Generator Version 2.0 - now with automatic video generation!<br/><br/>
    • <b>Generate complete songs with lyrics</b> – Create original songs from simple descriptions<br/>
    • <b>Custom or AI-generated lyrics</b> – Write your own lyrics or let AI create them for you<br/>
    • <b>Multiple music styles & genres</b> – From pop to rock, hip-hop to classical<br/>
    • <b>Automatic video creation</b> – Get stunning music videos automatically generated for your songs<br/>
    • <b>Professional quality output</b> – High-quality audio and video production<br/><br/>
    Create your own songs and music videos just like this one - all powered by AI!`,
    video:
      'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/song_video_40dbcec8-dc98-4f01-bd36-3c85a8a01f0e.mp4',
    isPortrait: true,
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator',
    sender: '🎬 New: Kling 2.5 Turbo Pro - Ultimate Video Generation',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Introducing Kling 2.5 Turbo Pro - the pinnacle of AI video generation technology!<br/><br/>
    • <b>Top-tier image-to-video generation</b> – Transform your images with unparalleled precision<br/>
    • <b>Unmatched motion fluidity</b> – Experience seamless, lifelike animations<br/>
    • <b>Cinematic visuals</b> – Professional-grade output with stunning visual quality<br/>
    • <b>Exceptional prompt precision</b> – Advanced understanding for accurate video creation<br/>
    • <b>Text & Image to Video</b> – Support for both text prompts and image transformation<br/><br/>
    Experience the future of AI video generation with Kling 2.5 Turbo Pro!`,
    video:
      'https://storage.googleapis.com/falserverless/model_tests/kling/kling-v2.5-turbo-pro-image-to-video-output.mp4',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🔍 New: Perplexity Sonar Models - Real-Time Search AI',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Experience the power of real-time web search integrated with AI responses!<br/><br/>
    • <b>Sonar Pro</b> – Advanced online LLM with comprehensive web search capabilities<br/>
    • <b>Sonar</b> – Fast and efficient web-aware model for quick searches<br/>
    • <b>Real-time Citations</b> – Get up-to-date information with source links<br/>
    • <b>Visual Results</b> – View relevant images alongside your answers<br/>
    • <b>Academic Mode</b> – Switch to academic search for research papers<br/><br/>
    Perfect for research, fact-checking, and staying informed with the latest information!`,
  },
  {
    href: '/ai-talking-videos',
    landingPageHref: '/ai-talking-video-generator',
    sender: '🎬 Create stunning talking avatar videos in seconds!',
    time: 'now',
    video: 'ht972WtwNTk',
    isPortrait: true,
    datetime: new Date().toISOString(),
    preview: `Transform static images into engaging videos effortlessly!<br/><br/>
    ✨ Upload your own photo OR generate one with our AI Image Generator<br/>
    🎤 Choose from 40+ languages & accents<br/>
    📝 Add your script and watch it come to life<br/>
    📚 Automatically generate TikTok-style captions<br/>
    📱 Perfect for TikTok, Instagram, and social media content<br/><br/>
    Create professional talking videos for marketing, education, or entertainment - no video editing skills required!`,
  },
  {
    href: '/ai-videos',
    landingPageHref: '/ai-video-generator',
    sender: '🎬 Google Veo 3 Enhanced - Now with Portrait Support!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Google Veo 3 just got even better with major improvements!<br/><br/>
    • <b>Portrait Videos (9:16)</b> – Perfect for social media platforms like TikTok, Instagram Reels, and YouTube Shorts<br/>
    • <b>Text-to-Video & Image-to-Video</b> – Create stunning videos from text prompts or transform your images<br/>
    • <b>Veo 3 Fast</b> – Lightning-fast generation without compromising quality<br/>
    • <b>50% Cheaper</b> – More affordable than ever for premium video generation<br/>
    • <b>1080p Quality</b> – High-definition output for professional results<br/><br/>
    Experience the future of AI video generation with enhanced Veo 3!`,
    video: '03h7kxEcLco',
    isPortrait: true,
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender: '🚀 New: Seedream 4.0 & Seededit 4.0 for Images!',
    time: 'now',
    image:
      'https://pbs.twimg.com/media/G0cJeJAbgAYE09w?format=jpg&name=4096x4096',
    datetime: new Date().toISOString(),
    preview: `Experience the latest advancements in AI image generation and editing!<br/><br/>
    • <b>Seedream 4.0</b> – Cutting-edge text-to-image generation with enhanced creativity and quality<br/>
    • <b>Seededit 4.0</b> – Revolutionary image editing capabilities for precise modifications and transformations<br/>
    • <b>Advanced AI Technology</b> – Powered by state-of-the-art neural networks for superior results<br/>
    • <b>Professional Quality</b> – Generate and edit images with industry-leading performance<br/><br/>
    Unlock new creative possibilities with Seedream 4.0 and Seededit 4.0!`,
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender: '🍌 New: Nano Banana Model - Best for Image Editing!',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Introducing Nano Banana - powered by Google's Gemini 2.5 Flash!<br/><br/>
    • <b>Best for Image Editing</b> – Superior performance in editing and transforming existing images<br/>
    • <b>Creative Excellence</b> – Exceptional prompt understanding for imaginative outputs<br/>
    • <b>Gemini-Powered</b> – Leverages Google's advanced Gemini 2.5 Flash technology<br/>
    • <b>Multi-Image Support</b> – Edit single or multiple images with precise control<br/>
    • <b>2 credits per image</b> – Premium quality at an affordable price<br/><br/>
    Transform and edit your images with unmatched precision using Nano Banana!`,
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender: '🚀 New: Google Imagen 4 Fast',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Experience lightning-fast image generation with Google's Imagen 4 Fast!<br/><br/>
    • <b>Imagen 4 Fast</b> – Google's fastest text-to-image model with Imagen 4 quality<br/>
    • Optimized for speed without compromising on quality<br/>
    • Perfect for rapid prototyping and quick creative iterations<br/>
    • Just 1 credit per image<br/><br/>
    Generate stunning images faster than ever before!`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🚀 New: Claude Opus 4.1 + Reasoning',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's latest Claude 4 models are now available!<br/><br/>
    • <b>Claude Opus 4.1</b> – Upgrade to Opus 4 for agentic tasks, real-world coding, and advanced reasoning<br/>
    • <b>Claude Opus 4.1 Reasoning</b> – Enhanced multi-step reasoning for complex problem solving<br/><br/>
    🧠 200k context window · 🌐 Internet access · 🎨 Image generation/editing · 🐍 Python`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🚀 New: GPT-5 Models',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `We've added OpenAI's next-generation GPT-5 family:<br/><br/>
    • <b>GPT-5</b> – The best model for coding and agentic tasks across domains (0.5x words)<br/>
    • <b>GPT-5 mini</b> – Faster, more cost-efficient for well-defined tasks (0.25x words)<br/>
    • <b>GPT-5 nano</b> – Fastest and most cost‑efficient; unlimited on eligible plans<br/><br/>
    All models support internet access, image generation/editing, and Python coding.`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🚀 New: Grok 4 - The Ultimate AI Model',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `We've added xAI's latest and greatest flagship model to our platform!<br/><br/>
    ✨ <b>Grok 4</b> - Offering unparalleled performance in natural language, math and reasoning<br/>
    🧠 The perfect jack of all trades for any task<br/>
    📚 Massive 256k context window for handling large documents<br/>
    ⚡ Supports internet access, image generation/editing, and Python coding<br/><br/>
    Experience the future of AI with Grok 4 - now available for users!`,
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender: '🚀 New: FLUX Kontext - Revolutionary AI Image Editing',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Big news! Easy-Peasy.AI now features FLUX Kontext - revolutionary AI image editing that understands both text AND images!<br/><br/>
    ✨ Edit images with simple text prompts<br/>
    🎭 Keep characters consistent across scenes<br/>
    🎯 Make precise local edits<br/>
    ⚡ Lightning-fast iterations<br/><br/>
    No more complex workflows - just describe what you want and watch the magic happen. Perfect for storytelling, brand content, and creative projects.`,
    video: 'IhD2p08qiiA',
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🚀 New: Deepseek R1 0528',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Deepseek R1 0528 is here. Deepseek's latest model with improved reasoning and coding capabilities`,
  },
  {
    href: '/chat',
    landingPageHref: '/marky',
    sender: '🚀 New: Claude 4 Models',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `Anthropic's latest generation of models, Claude 4, is here:<br/><br/>
    • <b>Claude Opus 4</b> - Anthropic's most capable and intelligent model yet. Claude Opus 4 sets new standards in complex reasoning and advanced coding<br/><br/>
    • <b>Claude Sonnet 4</b> - Anthropic's high-performance model with exceptional reasoning and efficiency<br/><br/>
    • <b>Claude Opus 4 Reasoning</b> - Anthropic's most powerful reasoning model for highly complex tasks<br/><br/>
    • <b>Claude Sonnet 4 Reasoning</b> - Anthropic's balanced reasoning model for enterprise workloads<br/>`,
  },
  {
    href: '/ai-images',
    landingPageHref: '/ai-image-generator',
    sender: '🎨 New: Google Imagen 4',
    time: 'now',
    datetime: new Date().toISOString(),
    preview: `We've added Google's latest text-to-image model, Imagen 4!<br/>
    • Advanced capabilities for stunning image generation<br/>
    • Explore its creative potential in our AI Image Generator<br/>
    • Supports various aspect ratios for versatile outputs<br/>
    Try it now!<br/><br/>`,
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/58c11fde-e64a-42f8-bff3-d360d0ad0397.png',
  },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: '🎨 New: Ideogram v3 with Enhanced Quality',
  //   time: 'now',
  //   datetime: '2024-08-05T10:30',
  //   preview: `We've integrated the powerful Ideogram v3 model:<br/>
  //   \n• Superior text rendering and creative compositions<br/>
  //   \n• Multiple aspect ratio options including square, portrait, and landscape<br/>
  //   \n• HD mode for highest quality images<br/>
  //   \nTry it now in our AI Image Generator!`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: '🚀 New: OpenAI GPT Image 1 with Editing!',
  //   time: '1d ago',
  //   datetime: '2024-07-25T10:30',
  //   preview: `Introducing <b>OpenAI GPT Image 1</b>, a powerful new model for both image generation and editing!<br/>
  //   \n• Generate high-quality images from text prompts.<br/>
  //   \n• <b>Edit existing images</b> by describing the changes you want.<br/>
  //   \n• Supports various sizes and quality levels.<br/>
  //   \n• Try it now in the AI Image Generator!`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🚀 New: OpenAI o-series Models',
  //   time: '1d ago',
  //   datetime: '2024-07-20T10:00',
  //   preview: `We've added OpenAI's latest reasoning models to our platform:<br/>
  //   \n• <b>o3</b> – A well-rounded and powerful model across domains. Sets a new standard for math, science, coding, and visual reasoning tasks. Excels at technical writing and instruction-following.<br/>
  //   \n• <b>o4-mini</b> – OpenAI's latest small o-series model. Optimized for fast, effective reasoning with exceptionally efficient performance in coding and visual tasks.<br/>
  //   \nBoth models feature a 200k context window and support reasoning, Python coding, internet access, and image generation/editing capabilities.`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🚀 New: GPT-4.1 Models',
  //   time: '1d ago',
  //   datetime: '2024-07-16T10:00',
  //   preview: `We've added OpenAI's latest GPT-4.1 models to our platform:<br/>
  //   \n• <b>GPT-4.1</b> – OpenAI's flagship model for complex tasks, well suited for problem solving across domains.<br/>
  //   \n• <b>GPT-4.1 mini</b> – Provides a balance between intelligence, speed, and cost for many use cases.<br/>
  //   \n• <b>GPT-4.1 nano</b> – The fastest, most cost-effective GPT-4.1 model.<br/>
  //   \nAll models support internet access, image generation/editing, and Python coding.`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🚀 New: Grok 3 Models',
  //   time: '1d ago',
  //   datetime: '2024-07-15T14:30',
  //   preview: `We've added xAI's latest models to our platform:<br/>
  //   \n• Grok 3 - xAI's flagship model excelling at enterprise use cases like data extraction, coding, and text summarization<br/>
  //   \n• Grok 3 Mini - A lightweight model that thinks before responding, great for simple or logic-based tasks<br/>
  //   \n• Both models support internet access, image generation/editing, and Python coding`,
  // },
  // {
  //   href: '/image-tools',
  //   landingPageHref: '/ai-image-generator',
  //   sender: '🎨 New: Ghiblify Image Transformer',
  //   time: '1d ago',
  //   datetime: '2024-07-12T16:35',
  //   preview: `Transform your photos into Studio Ghibli-style artwork with our new Ghiblify tool!<br/>
  //   \n• Convert ordinary photos into enchanting Ghibli-inspired scenes<br/>
  //   \n• Brings the magical aesthetic of Studio Ghibli to your images<br/>
  //   \n• Perfect for creating whimsical profile pictures or creative content<br/>
  //   \n• Available now in our Image Tools collection!`,
  //   image: '/img/tools/japanese.png',
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🚀 New: Gemini 2.5 Pro',
  //   time: '1d ago',
  //   datetime: '2024-04-10T16:35',
  //   preview: `We've added Google's most advanced model yet:<br/>
  //   \n• Gemini 2.5 Pro - Google's state-of-the-art thinking model for complex reasoning tasks<br/>
  //   \n• Excels at reasoning over complex problems in code, math, and STEM<br/>
  //   \n• Capable of analyzing large datasets, codebases, and documents using long context<br/>
  //   \n• Supports internet access, image generation/editing, and Python coding`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🚀 New: GPT-4.5 ',
  //   time: '1d ago',
  //   datetime: '2024-04-05T16:35',
  //   preview: `We've added OpenAI's most advanced model yet:<br/>
  //   \n• GPT-4.5 - OpenAI's largest and most capable GPT model with deep world knowledge and better understanding of user intent<br/>
  //   \n• Excels at creative tasks, agentic planning, writing, learning, and exploring new ideas<br/>
  //   \n• Accepts both text and image inputs<br/>
  //   \n• Supports tools calling and has access to the internet<br/>
  //   \n• Available now with 128k context window!`,
  // },
  // {
  //   href: '/agents',
  //   landingPageHref: '/chatbots',
  //   sender: '🤖 New: Facebook Messenger & Slack Integrations for Bots',
  //   time: '1d ago',
  //   datetime: '2024-04-02T16:35',
  //   preview: `We've added two powerful new integrations for your bots:<br/>
  //   \n• Facebook Messenger - Connect your bot to your Facebook Page to engage with customers directly on Messenger<br/>
  //   \n• Slack - Add your bot to Slack workspaces to provide instant assistance to your team<br/>
  //   \n• Easily manage connected workspaces and channels<br/>
  //   \n• Toggle integrations on/off as needed<br/>
  //   \nTry them now in your bot's integration settings!`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🧠 New: Claude 3.7 Sonnet Models',
  //   time: '1d ago',
  //   datetime: '2024-04-01T16:35',
  //   preview: `We've added Anthropic's most intelligent models to date:<br/>
  //   \n• Claude 3.7 Sonnet - The first hybrid reasoning model from Anthropic with state-of-the-art performance<br/>
  //   \n• Claude 3.7 Sonnet Reasoning - Enhanced version with specialized reasoning capabilities<br/>
  //   \n• Both models excel at coding, content generation, data analysis, and planning tasks<br/>
  //   \n• Available now with 200k context window and support for internet access, image generation/editing, and Python coding`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: '🎨 New: Google Imagen 3.0',
  //   time: '1d ago',
  //   datetime: '2024-03-30T16:35',
  //   preview: `We've added Google's latest image model:<br/>
  //   \n• Imagen 3.0 - Google's most advanced text-to-image model<br/>
  //   \n• Supports multiple aspect ratios (1:1, 3:4, 4:3, 9:16, 16:9)<br/>
  //   \n• Generate up to 4 images at once<br/>
  //   \n• Superior quality for photorealistic images<br/>
  //   \n• Available now in our Image Generator!`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🧠 New: Gemini Flash 2.0',
  //   time: '1d ago',
  //   datetime: '2024-03-29T16:35',
  //   preview: `We've added Google's latest model:<br/>
  //   \n• Gemini Flash 2.0 - Fast and versatile multimodal model for scaling across diverse tasks<br/>
  //   \n• Available for free to all users<br/>
  //   \n• Supports internet access, image generation/editing, and Python coding`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🧠 New: o3-mini Reasoning Model',
  //   time: '1d ago',
  //   datetime: '2024-03-28T16:35',
  //   preview: `We've added OpenAI's latest reasoning model:<br/>
  //   \n• o3-mini - Latest reasoning model optimized for exceptional performance in mathematics, coding, and scientific analysis while maintaining fast response times<br/>
  //   \n• Features 200k context window and advanced reasoning capabilities<br/>
  //   \n• Excels at complex problem-solving tasks while being more efficient than larger models`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🧠 New Reasoning Models Added!',
  //   time: '1d ago',
  //   datetime: '2024-03-27T16:35',
  //   preview: `We've added three powerful new reasoning models:<br/>
  //   \n• o1-mini - A faster and more affordable reasoning model from OpenAI, particularly adept at coding, math, and science tasks<br/>
  //   \n• o1 - OpenAI's most powerful reasoning model, designed to solve hard problems using broad general knowledge<br/>
  //   \n• Deepseek R1 - State-of-the-art model optimized with reinforcement learning for exceptional reasoning, math, and code performance`,
  // },
  // {
  //   href: '/ai-videos',
  //   landingPageHref: '/ai-video-generator',
  //   sender: '🎬 New: Kling 1.6 Video Generation',
  //   time: '1d ago',
  //   datetime: '2024-03-27T16:35',
  //   preview: `Experience our latest video generation models! Kling 1.6 brings exciting improvements:<br/>
  //   \n• Kling 1.6 Pro - Advanced model for superior quality videos<br/>
  //   \n• Kling 1.6 Standard - Latest standard version with improved capabilities<br/>
  //   \n• Support for both text-to-video and image-to-video<br/>
  //   \n• Enhanced motion quality and transitions<br/>
  //   \nTry them now in our AI Video Generator!`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🚀 Two New Powerful Models Added!',
  //   time: '1d ago',
  //   datetime: '2024-03-27T16:35',
  //   preview: `We've added two exciting new models:<br/>
  //   \n• Deepseek V3 - Chinese open-source model that outperforms industry giants like OpenAI and Meta in code generation and understanding. Has 128k context window.<br/>
  //   \n• Llama 3.3 70B - Meta's latest model that outperforms many available open source chat models on common industry benchmarks. Has 128k context window.`,
  // },
  // {
  //   href: '/agents',
  //   landingPageHref: '/chatbots',
  //   sender: '🤖 New: Bot Notifications & Daily Summaries',
  //   time: '1d ago',
  //   datetime: '2024-03-27T16:35',
  //   preview: `We've added powerful notification features for your bots:<br/>
  //   \n• Get instant notifications for new leads<br/>
  //   \n• Receive daily summaries of your bot's conversations<br/>
  //   \n• Customize email templates for lead notifications<br/>
  //   \n• Support for multiple notification recipients<br/>
  //   \nManage your notifications in the bot settings!`,
  // },
  // {
  //   href: '/ai-videos',
  //   landingPageHref: '/ai-video-generator',
  //   sender: '🎬 AI Video Generator: Sound Effects & Longer Videos',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `We've added exciting new features to our AI Video Generator:<br/>
  //   \n• AI-generated sound effects and background music for your videos<br/>
  //   \n• Support for 10-second video generation (2x longer!)<br/>
  //   \n• Duration options vary by model - some models now support both 5 and 10 second videos<br/>
  //   \n• Note: 10-second videos consume 2x credits due to increased processing requirements`,
  // },
  // {
  //   href: '/ai-video',
  //   landingPageHref: '/ai-video-generator',
  //   sender: '🎬 New: Kling 1.5 Pro Video Generation',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `Experience our latest video generation model! Kling 1.5 Pro brings exciting new capabilities:<br/>
  //   \n• Generate videos from images or text prompts<br/>
  //   \n• Enhanced motion quality and smoothness<br/>
  //   \n• Better character animations<br/>
  //   \n• Advanced quality control features<br/>
  //   \nTry it now in our AI Video Generator!`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: '🎨 New: FLUX 1.1 Pro Ultra with Raw Mode',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `Experience our most advanced image model yet! FLUX 1.1 Pro Ultra introduces Raw Mode - a groundbreaking feature for creators seeking authenticity:<br/>
  //   \n• Less synthetic, more natural aesthetic<br/>
  //   \n• Enhanced diversity in human subjects<br/>
  //   \n• Superior realism in nature photography<br/>
  //   \n• Available now in our paid plans`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: 'Two new models added to Chat with Marky!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `We've added two exciting new models:<br/>• Claude 3.5 Haiku - Next generation of Anthropic's fastest model. Surpasses Claude 3 Opus on many intelligence benchmarks.<br/>• Grok - xAI's witty AI assistant inspired by The Hitchhiker's Guide to the Galaxy. Excels at answering questions with humor and suggesting insightful questions to ask.`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: '🎨 New: AI Caricature Generator',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `Transform your photos into fun caricature art with AI! Here's how:<br/>
  //   \n1. Go to AI Images<br/>
  //   \n2. Upload your image<br/>
  //   \n3. Select "Caricature" action<br/>
  //   \n4. Click Generate<br/>
  //   \nCreate playful, artistic caricatures from any portrait photo!`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: '🎨 New: AI Background Remover',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `Remove backgrounds from your images in seconds with AI! Here's how:<br/>
  //   \n1. Go to AI Images<br/>
  //   \n2. Upload your image<br/>
  //   \n3. Select "Remove Background" action<br/>
  //   \n4. Click Generate<br/>
  //   \nGet clean, professional-looking images with just one click!`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: 'Recraft V3 (red_panda) is now available!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'The new top-ranking image model is now available. Experience superior image generation quality and enhanced creative capabilities.',
  // },
  // {
  //   href: '/ai-videos',
  //   landingPageHref: '/ai-video-generator',
  //   sender: '🎬 Generate Videos from Images!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `You can now generate videos from images created in Image Generator or upload your own images to AI videos page.\n\nAvailable models:\n• Luma Dream Machine\n• Runway Gen3 Alpha\n• Kling 1.0\n• Hailuo AI`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: 'Stable Diffusion 3.5 Large has arrived!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'Generate higher-resolution images with finer details and more variety with the new model from Stability AI.',
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: 'Ideogram AI is now available!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'A beautiful model, especially when creating designs with text. Try it out for stunning text-integrated visuals.',
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: '🎨 Image Editing Just Got Magical!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `✨ Simply draw on any image to highlight areas you want to change\n💡 Add your prompt\n🪄 Let AI do its magic`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: 'FLUX1.1 Pro is here 🫐📷',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `New Flux model produces higher quality images than it's predecessor FLUX.1 [pro], while being up to 6 times faster.\n\nTo use it in our Image Generator - just enable HD mode toggle when using the Flux model.`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: '🌎 All Chat Models Now Have Internet Access!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'Now all models can access the latest data from the internet on Chat with Marky. Including Claude 3.5 Sonnet, GPT-4o, Meta Llama, Google Gemini, and more.',
  // },
  // {
  //   href: '/ai-images/models',
  //   landingPageHref: '/ai-photo-generator',
  //   sender: 'Train AI on Your Personal Images!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `It's pretty easy: Gather some images of yourself, create a new model, then use the new model in Image Generator to generate novel images of yourself as a superhero, a cartoon character, an adventurer, or just... a regular person.`,
  // },
  // {
  //   href: '/agents',
  //   landingPageHref: '/guide/bots-integration-with-wordpress',
  //   sender: 'New Integration: Bots + WordPress',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'Connect your chatbot to WordPress website and provide your customers with instant support.',
  // },
  // {
  //   href: '/ai-interior-designer',
  //   landingPageHref: '/ai-interior-design-generator',
  //   sender: 'Transform Sketches into Photorealistic Renders',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: `With Easy-Peasy AI's Interior Design Generator, you can now create photorealistic renders from your raw interior design sketches. Imagine doing a quick draft of an idea you have. Upload it to Easy-Peasy AI and ~30 seconds later you have a realistic AI photo of how it'd look in real life. A photorealistic render you can use to show to your clients so they can imagine how it looks better.`,
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: 'Meta Llama 3.1 405B is now available!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     "Meta's biggest version of its open source Llama. Is competitive with leading foundation models across a range of tasks including GPT-4, GPT-4o and Claude 3.5 Sonnet.",
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: 'Claude 3.5 Sonnet is now available!',
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'Most intelligent model from Anthropic. Has 200k context window. Trained till April 2024 data.',
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `We have just added Google Flash and Google 1.5 Pro`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     "The latest and most advanced models from Google. Google Flash is fast and versatile for scaling across diverse tasks, while Google 1.5 Pro is Google's largest and most capable AI model. Try them out and let us know what you think. We are excited to hear your feedback!",
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `We have just added GPT-4o`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'Most advanced system from OpenAI. 2x faster than GPT-4 Turbo. Additionally, GPT-4o has the best vision and performance across non-English languages of any of our models. Try it out and let us know what you think. We are excited to hear your feedback!',
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `We have added Claude 3 Opus, Claude 3 Sonnet, Mistral Large and Google Gemini Pro models`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'Try them out and let us know what you think. We are excited to hear your feedback!',
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: `Kandinsky 3.0 model added to the Image Generator!`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   image: 'https://pbs.twimg.com/media/GFuGmbbaQAAv_1P?format=jpg&name=small',
  // },
  // {
  //   href: '/presets/blog-post-v3',
  //   landingPageHref: '/templates/blog-post',
  //   sender: `Two new chat models added! Meta Llama 2 70B and Mixtral-8x7B from Mistral AI`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  // },
  // {
  //   href: '/presets/blog-post-v3',
  //   landingPageHref: '/templates/blog-post',
  //   sender: `One-click Article Generator V3`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'Write long, real-time, factual articles with the click of a button. Featured image included.',
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `GPT-4 Turbo With Vision is now available!`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'You can now upload images and ask the AI any questions about them. This includes generating social media captions, analyzing screenshots, and interpreting photos.',
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `We have added Claude 2.1 model`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     'It has 200,000 context window and 2x Decrease in Hallucination Rates.',
  // },
  // {
  //   href: '/tts',
  //   landingPageHref: '/text-to-speech',
  //   sender: `Voice Cloning: Clone Your Voice in Minutes`,
  //   preview: `<p class='mb-5'>Now you can instantly clone your voice using AI and generate text-to-speech audios that sound just like you.</p>`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   video: 'Zhy4sLmuCKQ',
  // },
  // {
  //   href: '/audios',
  //   landingPageHref: '/ai-transcription',
  //   sender: `Now AI Transcription can produce content for you!`,
  //   preview: `<p class='mb-5'>We upgraded our AI Transcription to produce amazing content for you 10x faster. Just upload your audio or video file and get a summary, social media posts, article, newsletter and many more.</p>`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `GPT-4 Turbo`,
  //   preview: `<p class='mb-5'>GPT-4 Turbo, the latest GPT-4 model is now available on Easy-Peasy AI. It has 128k context window. Trained with data up to April 2023 and has the access to the latest data via web browsing.</p>`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: `New models added to the Image Generator!`,
  //   preview: `<p class='mb-5'>We've added 3 new models to our Image Generator DALL-E 3, Stable Diffusion XL, Kandinsky. The most advanced model, DALL-E 3 is proficient in rendering complex features like text, hands, and faces with remarkable accuracy.</p>`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   image: 'https://pbs.twimg.com/media/F-9Cl-magAAc-pK?format=jpg&name=small',
  // },
  // {
  //   href: '/voices',
  //   sender: `Custom instructions for AI outputs`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   video: 'vF-rMaCd26g',
  //   preview: 'custom_instructions',
  // },
  // {
  //   href: '/audios',
  //   landingPageHref: '/ai-transcription',
  //   sender: `Record and Transcribe Audios`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   video: 'xIV5zuJRD4A',
  //   preview: 'audio_recording',
  // },
  // {
  //   href: '/tts',
  //   landingPageHref: '/text-to-speech',
  //   sender: `AI Text-To-Speech Generator`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   video: 'uWbLpE5f6mQ',
  //   preview: 'tts',
  // },
  // {
  //   href: '/audios',
  //   landingPageHref: '/ai-transcription',
  //   sender: `Transcribe YouTube videos with AI`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   video: 'F2m3Hnf57mE',
  //   preview:
  //     "<p class='mb-5'>Just paste the URL to get an automated transcription in seconds. Harness the power of AI to convert speech to text!</p>",
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `GPT-4 32K and Claude 2 are now available on our platform!`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: 'gpt-4_32k',
  //   image: 'https://pbs.twimg.com/media/F4WAZIhaEAAkQHi?format=jpg&name=small',
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: `Download the generated images in 4K format`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: ` <p><img class="rounded-xl" src='https://pbs.twimg.com/media/F1fgcCFaAAAOtYe?format=jpg&name=small' /></p>`,
  // },
  // {
  //   href: '/ai-images',
  //   landingPageHref: '/ai-image-generator',
  //   sender: `Generate your own stickers with AI`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   video: 'NbTSpJkvSPg',
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `Introducing Chat Personas`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   video: '0kZBU2jPU3g',
  //   preview: 'chat_personas',
  // },
  // {
  //   href: '/presets/native-speaker',
  //   landingPageHref: '/templates/native-speaker',
  //   sender: `Templates Improvements`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: 'native_grammar',
  //   image: 'https://pbs.twimg.com/media/Fx6FLa8aEAETTAC?format=jpg&name=small',
  // },
  // {
  //   href: '/chat',
  //   landingPageHref: '/marky',
  //   sender: `Chat with PDF, Docx, TXT files`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview: 'news_pdf',
  //   video: 'xrgdrewr7B8',
  // },
  // {
  //   href: '/audios',
  //   landingPageHref: '/ai-transcription',
  //   sender: `AI Audio Transcription with speakers detection`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   preview:
  //     "<p class='mb-5'>Now the transcription and summary are much more correct and clean. </p>",
  //   video: 'EKS3S0XOGNs',
  // },
  // {
  //   href: '/presets/dynamic',
  //   landingPageHref: '/templates/dynamic',
  //   sender: `Dynamic Templates`,
  //   time: '1d ago',
  //   datetime: '2021-01-27T16:35',
  //   video: '_SXqQjq1ZqA',
  // },
];

// Image Carousel Component
function ImageCarousel({ images }: { images: string[] }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className='relative mx-auto mt-4 max-w-2xl'>
      <div className='relative aspect-[4/3] overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-700 shadow-lg'>
        {/* Image slides */}
        {images.map((image, index) => (
          <div
            key={index}
            className='absolute inset-0 transition-opacity duration-700'
            style={{
              opacity: index === currentIndex ? 1 : 0,
              pointerEvents: index === currentIndex ? 'auto' : 'none',
            }}
          >
            <img
              src={image}
              alt={`Sample ${index + 1}`}
              className='h-full w-full object-cover'
              loading='lazy'
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        ))}
      </div>

      {/* Indicators */}
      {images.length > 1 && (
        <div className='mt-3 flex justify-center gap-2'>
          {images.map((_, index) => (
            <button
              key={index}
              type='button'
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className='group'
              aria-label={`View image ${index + 1}`}
            >
              <div
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex
                    ? 'bg-primary-600 dark:bg-primary-400 w-8'
                    : 'bg-gray-300 dark:bg-gray-600 w-2 group-hover:bg-gray-400 dark:group-hover:bg-gray-500'
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WhatsNew({ isLandingPage = false }) {
  const { t } = useLocale('whats_new');

  return (
    <div
      id='whats-new'
      data-whats-new
      className='mt-5 bg-white shadow dark:bg-slate-800 sm:rounded-lg '
    >
      <div className='px-4 py-5 sm:p-6'>
        <h3 className='text-lg font-medium leading-6 text-gray-900 dark:text-slate-300'>
          {t(`What's New`)}
        </h3>
        <div className='mx-auto mt-2 text-sm text-gray-500'>
          <ul role='list' className='divide-y dark:divide-slate-700'>
            {messages.map((message) => (
              <li
                key={message.sender}
                // onClick={() => {
                //   router.push(message.href);
                // }}
                className='cursor-pointer px-4 py-10 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-600 hover:bg-gray-50 dark:hover:bg-slate-900/5'
              >
                <div className=' '>
                  <Link
                    href={
                      isLandingPage && message.landingPageHref
                        ? message.landingPageHref
                        : message.href
                    }
                    className='text-xl font-medium text-primary-700'
                    {...(message.openInNewTab
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {t(message.sender)}
                  </Link>
                </div>
                <div className='mt-1'>
                  {message.preview && (
                    <p
                      className='text-md text-gray-600 dark:text-slate-400'
                      dangerouslySetInnerHTML={{ __html: t(message.preview) }}
                    ></p>
                  )}

                  {message.video && (
                    <div
                      style={{
                        position: 'relative',
                        paddingBottom: message.isPortrait ? '75%' : '56.25%',
                        height: 0,
                        overflow: 'hidden',
                        maxWidth: message.isPortrait ? '315px' : '100%',
                        // The top margin sits on the wrapper: on the absolutely
                        // positioned video it pushed the player down and clipped
                        // its controls at the bottom.
                        margin: message.isPortrait ? '1rem auto 0' : '1rem 0 0',
                      }}
                    >
                      <LazyMedia>
                        {message.video.startsWith('http') ? (
                          <video
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                            }}
                            poster={message.poster}
                            controls
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload='none'
                          >
                            <source src={message.video} type='video/mp4' />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <iframe
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                            }}
                            src={`https://www.youtube.com/embed/${message.video}`}
                            title='YouTube video player'
                            frameBorder='0'
                            loading='lazy'
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                            allowFullScreen
                          ></iframe>
                        )}
                      </LazyMedia>
                    </div>
                  )}
                  {message.image && (
                    <LazyMedia>
                      {message.image.includes('media.easy-peasy.ai') ? (
                        <Image
                          className='rounded-xl'
                          src={message.image}
                          alt={message.sender}
                          width={800}
                          height={600}
                          style={{ width: '100%', height: 'auto' }}
                        />
                      ) : (
                        <img
                          className='rounded-xl'
                          src={message.image}
                          alt={message.sender}
                          loading='lazy'
                          decoding='async'
                        />
                      )}
                    </LazyMedia>
                  )}
                  {message.sampleImages && message.sampleImages.length > 0 && (
                    <LazyMedia>
                      <ImageCarousel images={message.sampleImages} />
                    </LazyMedia>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
